import dayjs from 'dayjs';
import {
  DASHBOARD_DATA_STATE,
  DASHBOARD_METRIC_STATUS,
  DASHBOARD_WIDGET_TARGET_MODE,
  DASHBOARD_WIDGET_TYPE,
  DASHBOARD_VALUE_TYPE
} from './dashboardContract';
import { sortDashboardVisualizations } from './dashboardVisualizationModel';
import { sortDashboardWidgets } from './dashboardWidgetModel';

const DEFAULT_RANGE = Object.freeze({
  [DASHBOARD_VALUE_TYPE.PERCENTAGE]: { minimum: 0, maximum: 100 },
  [DASHBOARD_VALUE_TYPE.INTEGER]: { minimum: 0, maximum: 10 },
  [DASHBOARD_VALUE_TYPE.DECIMAL]: { minimum: 0, maximum: 100 }
});

const finiteOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const getSampleBounds = (metric) => {
  const defaults =
    DEFAULT_RANGE[metric.valueType] ??
    DEFAULT_RANGE[DASHBOARD_VALUE_TYPE.DECIMAL];
  let minimum = finiteOrNull(metric.minimum);
  let maximum = finiteOrNull(metric.maximum);

  if (minimum === null && maximum === null) return defaults;
  if (minimum === null) minimum = Math.min(0, maximum - 100);
  if (maximum === null) {
    maximum = Math.max(
      minimum + (metric.valueType === DASHBOARD_VALUE_TYPE.INTEGER ? 10 : 100),
      0
    );
  }

  return { minimum, maximum };
};

const createSampleValue = (metric, metricIndex, pointIndex) => {
  const { minimum, maximum } = getSampleBounds(metric);
  if (minimum === maximum) return minimum;

  const ratio = 0.34 + ((metricIndex * 3 + pointIndex * 2) % 7) * 0.08;
  const rawValue = minimum + (maximum - minimum) * ratio;
  const decimalPlaces =
    metric.valueType === DASHBOARD_VALUE_TYPE.INTEGER
      ? 0
      : Math.min(6, Math.max(0, Number(metric.decimalPlaces) || 0));
  const rounded = Number(rawValue.toFixed(decimalPlaces));

  return Math.min(maximum, Math.max(minimum, rounded));
};

const progressStatus = (widget, percentage) => {
  if (percentage === null) return null;
  if (percentage < widget.warningThreshold) return 'red';
  if (percentage < widget.successThreshold) return 'yellow';
  return 'green';
};

const buildPreviewWidgets = (widgets, latestMetrics) => {
  const latestByMetricId = new Map(
    latestMetrics.map((metric) => [metric.metricDefinitionId, metric])
  );

  return sortDashboardWidgets(widgets).map((widget) => {
    const metrics = [...(widget.metrics ?? [])]
      .sort((left, right) => left.seriesOrder - right.seriesOrder)
      .map((metric) => ({
        ...metric,
        ...(latestByMetricId.get(metric.metricDefinitionId) ?? {
          dataState: DASHBOARD_DATA_STATE.NO_DATA,
          effectiveDate: null,
          metricValueId: null,
          metricValueRevisionId: null,
          value: null
        })
      }));
    const available = metrics.filter(
      (metric) => metric.dataState === DASHBOARD_DATA_STATE.AVAILABLE
    );
    const dataState =
      available.length === 0
        ? DASHBOARD_DATA_STATE.NO_DATA
        : available.length === metrics.length
          ? DASHBOARD_DATA_STATE.AVAILABLE
          : DASHBOARD_DATA_STATE.PARTIAL_DATA;

    if (widget.widgetType === DASHBOARD_WIDGET_TYPE.NUMERIC) {
      return {
        ...widget,
        dataState,
        metrics,
        calculation: {
          calculationType: DASHBOARD_WIDGET_TYPE.NUMERIC,
          effectiveDate: metrics[0]?.effectiveDate ?? null,
          value: metrics[0]?.value ?? null
        }
      };
    }

    if (widget.widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS) {
      const targetValue =
        widget.targetMode === DASHBOARD_WIDGET_TARGET_MODE.METRIC_MAXIMUM
          ? Number(metrics[0]?.maximum ?? 0)
          : Number(widget.targetValue ?? 0);
      const value = metrics[0]?.value;
      const percentage =
        value === null || value === undefined || targetValue <= 0
          ? null
          : Math.round(
              (Math.min(100, Math.max(0, (value / targetValue) * 100)) +
                Number.EPSILON) *
                100
            ) / 100;
      return {
        ...widget,
        dataState,
        metrics,
        targetValue,
        calculation: {
          calculationType: DASHBOARD_WIDGET_TYPE.PROGRESS,
          effectiveDate: metrics[0]?.effectiveDate ?? null,
          percentage,
          progressStatus: progressStatus(widget, percentage),
          targetValue,
          value: value ?? null
        }
      };
    }

    return {
      ...widget,
      dataState,
      metrics,
      calculation: {
        calculationType: 'distribution',
        totalValue: available.length
          ? available.reduce((total, metric) => total + metric.value, 0)
          : null
      }
    };
  });
};

export const buildDashboardPreviewSampleData = (
  draftVersion,
  referenceDate = dayjs()
) => {
  const previewDate = dayjs(referenceDate);
  const activeMetrics = [...(draftVersion?.metrics ?? [])]
    .filter(
      (metric) => metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ACTIVE
    )
    .sort(
      (left, right) =>
        left.displayOrder - right.displayOrder ||
        left.label.localeCompare(right.label)
    );

  const seriesMetrics = activeMetrics.map((metric, metricIndex) => {
    const points = Array.from({ length: 7 }, (_item, pointIndex) => ({
      effectiveDate: previewDate
        .subtract(6 - pointIndex, 'day')
        .format('YYYY-MM-DD'),
      metricValueId: 'preview-' + metric.metricDefinitionId + '-' + pointIndex,
      metricValueRevisionId:
        'preview-revision-' + metric.metricDefinitionId + '-' + pointIndex,
      value: createSampleValue(metric, metricIndex, pointIndex)
    }));

    return {
      ...metric,
      aggregateValue: points[points.length - 1].value,
      dataState: DASHBOARD_DATA_STATE.AVAILABLE,
      points
    };
  });

  const latestMetrics = seriesMetrics.map((metric) => {
    const latestPoint = metric.points[metric.points.length - 1];
    return {
      dataState: DASHBOARD_DATA_STATE.AVAILABLE,
      effectiveDate: latestPoint.effectiveDate,
      metricDefinitionId: metric.metricDefinitionId,
      metricValueId: latestPoint.metricValueId,
      metricValueRevisionId: latestPoint.metricValueRevisionId,
      value: latestPoint.value
    };
  });

  const widgets = buildPreviewWidgets(
    draftVersion?.widgets ?? [],
    latestMetrics
  );

  return {
    activeMetrics,
    fromDate: previewDate.subtract(6, 'day').format('YYYY-MM-DD'),
    latestMetrics,
    seriesMetrics,
    toDate: previewDate.format('YYYY-MM-DD'),
    visualizations: sortDashboardVisualizations(
      draftVersion?.visualizations ?? []
    ),
    widgets
  };
};
